'use client';

import type { AnimationItem } from 'lottie-web';
import { useEffect, useRef } from 'react';

type Props = {
  /** `public/` 기준 JSON 경로. 런타임 fetch 라 JS 번들에 포함되지 않는다. */
  src: string;
  className?: string;
  loop?: boolean;
  autoplay?: boolean;
};

/**
 * Lottie 재생 컨테이너.
 *
 * 플레이어는 `lottie_light`(셰이프 전용 경량 빌드)를 동적 import 한다 —
 * 전 페이지 상시 노출되는 플로팅 버튼이 초기 번들을 키우지 않도록.
 * 표현식·이미지·텍스트 레이어가 포함된 파일은 이 빌드로 재생되지 않는다.
 *
 * 장식 요소이므로 `aria-hidden` 이다. 의미가 있는 경우 호출부 버튼에 label 을 준다.
 */
export function LottieAnimation({
  src,
  className,
  loop = true,
  autoplay = true,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<AnimationItem | null>(null);

  useEffect(() => {
    let cancelled = false;

    void import('lottie-web/build/player/lottie_light').then(
      ({ default: lottie }) => {
        if (cancelled || !containerRef.current) return;
        animationRef.current = lottie.loadAnimation({
          container: containerRef.current,
          renderer: 'svg',
          loop,
          autoplay,
          path: src,
        });
      },
    );

    return () => {
      cancelled = true;
      animationRef.current?.destroy();
      animationRef.current = null;
    };
  }, [src, loop, autoplay]);

  return <div ref={containerRef} className={className} aria-hidden />;
}
